import { cva } from 'class-variance-authority';

import Button from 'molecules/Button';
import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import Link from 'molecules/Link';
import RichText from 'molecules/RichText';

import { scrollToAnchor } from 'utils/scroll/scrollToAnchor';

import type { IconCardConfigProps } from 'organisms/Cards/IconCard/config';
import type React from 'react';
import type { FC } from 'react';

interface IconCardProps extends IconCardConfigProps {
  darkCard?: boolean;
}

const cardStyles = cva('flex size-full rounded-lg border p-5 backdrop-blur focus-outline-primary', {
  variants: {
    darkCard: {
      true: 'border-gray-300/50 bg-iconCardGradient',
      false: 'border-primary-200 bg-primary-50',
    },
  },
});

const artWrapper = cva('flex w-full items-start', {
  variants: {
    size: {
      sm: 'h-6',
      md: 'h-10',
      lg: 'h-16',
      xl: 'h-20',
    },
  },
  defaultVariants: { size: 'sm' },
});
const IconCard: FC<IconCardProps> = ({
  iconId,
  image,
  iconSize = 'sm',
  title,
  subhead,
  description,
  button,
  darkCard,
  isIcon = true,
  customTextHeading,
  useStandardBullets = false,
  pinButtonToBottom = true,
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>) => {
    if (button?.href?.startsWith('#')) {
      e.preventDefault();
      scrollToAnchor(button.href);
    }
  };

  const isAnchor = button?.href?.startsWith('#');
  const href = isAnchor ? undefined : button?.href;
  const artBox = artWrapper({ size: iconSize });

  const titleClass = darkCard
    ? 'text-xl font-bold leading-normal text-white'
    : 'text-xl font-bold leading-normal text-gray-900';

  const subheadClass = darkCard ? 'mt-1 text-lg leading-6 text-white' : 'mt-1 text-lg leading-6 text-gray-900';

  const baseDescriptionClass = darkCard ? 'text-md text-gray-300' : 'text-md text-gray-700';

  const iconClass = darkCard
    ? 'h-full w-auto stroke-[1.5] text-primary-200'
    : 'h-full w-auto stroke-[1.5] text-primary-700';

  const renderArt = () => {
    if (!isIcon) {
      return (
        customTextHeading && (
          <RichText
            text={customTextHeading}
            className={darkCard ? 'text-2xl font-medium text-primary-200' : 'text-2xl font-medium text-primary-700'}
          />
        )
      );
    }

    if (image?.file?.url) {
      return (
        <Image
          {...image.file}
          alt={image.alt || title || 'Card image'}
          url={image.file.url}
          className="h-full w-auto object-contain"
        />
      );
    }

    if (iconId) {
      return <Icon icon={iconId} className={iconClass} />;
    }

    return null;
  };

  const isInteractive = Boolean(button?.href);

  return (
    <Link
      href={href}
      aria-label={isInteractive ? title || button?.label : undefined}
      className={`${cardStyles({ darkCard: !!darkCard })} flex size-full flex-col gap-4 ${isInteractive ? 'cursor-pointer' : 'cursor-default'}`}
      onClick={isInteractive ? handleClick : undefined}
      enablePreview={false}
    >
      <div className={artBox}>{renderArt()}</div>

      {(title || subhead) && (
        <div className="flex flex-col">
          {title && <h3 className={titleClass}>{title}</h3>}
          {subhead && <p className={subheadClass}>{subhead}</p>}
        </div>
      )}

      {description && (
        <RichText text={description} className={baseDescriptionClass} isBulletStandard={useStandardBullets} />
      )}

      {button?.label && (
        <Button
          label={button.label}
          semanticFallback="div"
          hierarchy="linkColor"
          endIcon="arrow-up-right"
          className={pinButtonToBottom ? `mt-auto ${button.className}` : 'mt-2'}
        />
      )}
    </Link>
  );
};

export default IconCard;
